import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, AlertTriangle, MapPin, History, ArrowUpDown, Search, Download, Upload, BarChart3, Minus, Plus, RefreshCw } from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function InventoryManagementPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [location, setLocation] = useState('all');
  const [locations, setLocations] = useState([]);
  const [showAdjust, setShowAdjust] = useState(null);
  const [adjustQty, setAdjustQty] = useState(0);
  const [adjustReason, setAdjustReason] = useState('count');
  const [sortBy, setSortBy] = useState('quantity');

  useEffect(() => { loadData(); }, [location]);

  const loadData = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile.member);
      const [invRes, locRes] = await Promise.all([
        apiClient.get('/api/v1/inventory', { params: { location: location !== 'all' ? location : undefined } }),
        apiClient.get('/api/v1/inventory/locations'),
      ]);
      setInventory(invRes.data.inventory || []);
      setLocations(locRes.data.locations || []);
    } catch (err) {
      setInventory([
        { sku: 'WH-100-BLK', product_name: 'Wireless Headphones (Black)', quantity: 234, reorder_point: 50, safety_stock: 25, location: 'Warehouse A', status: 'in_stock', last_counted: '2026-03-05' },
        { sku: 'WH-100-WHT', product_name: 'Wireless Headphones (White)', quantity: 12, reorder_point: 50, safety_stock: 25, location: 'Warehouse A', status: 'low_stock', last_counted: '2026-03-05' },
        { sku: 'TS-OCT-M-BLU', product_name: 'Organic Cotton T-Shirt M Blue', quantity: 567, reorder_point: 100, safety_stock: 50, location: 'Warehouse B', status: 'in_stock', last_counted: '2026-03-04' },
        { sku: 'SH-HUB-PRO', product_name: 'Smart Home Hub Pro', quantity: 45, reorder_point: 30, safety_stock: 15, location: 'Warehouse A', status: 'low_stock', last_counted: '2026-03-06' },
        { sku: 'CF-ART-12OZ', product_name: 'Artisan Coffee Blend 12oz', quantity: 1200, reorder_point: 200, safety_stock: 100, location: 'Warehouse C', status: 'in_stock', last_counted: '2026-03-03' },
        { sku: 'RS-ULT-10-BLK', product_name: 'Running Shoes Ultra 10 Black', quantity: 0, reorder_point: 20, safety_stock: 10, location: 'Warehouse B', status: 'out_of_stock', last_counted: '2026-03-06' },
        { sku: 'HK-BCB-3PC', product_name: 'Bamboo Cutting Board Set', quantity: 89, reorder_point: 30, safety_stock: 15, location: 'Warehouse A', status: 'in_stock', last_counted: '2026-03-02' },
      ]);
      setLocations([
        { id: 'wh-a', name: 'Warehouse A', city: 'Los Angeles, CA', items: 3 },
        { id: 'wh-b', name: 'Warehouse B', city: 'Dallas, TX', items: 2 },
        { id: 'wh-c', name: 'Warehouse C', city: 'Chicago, IL', items: 1 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const adjustInventory = async (sku) => {
    try {
      await apiClient.patch(`/api/v1/inventory/${sku}`, { adjustment: adjustQty, reason: adjustReason });
      toast.success(`Inventory adjusted for ${sku}`);
      setShowAdjust(null);
      setAdjustQty(0);
      loadData();
    } catch (err) {
      toast.error('Adjustment failed');
    }
  };

  const lowStockCount = inventory.filter(i => i.status === 'low_stock').length;
  const outOfStockCount = inventory.filter(i => i.status === 'out_of_stock').length;

  const statusColor = { in_stock: 'text-[#3EF1C7]', low_stock: 'text-[#FFC857]', out_of_stock: 'text-[#F55454]' };
  const statusBg = { in_stock: 'bg-[#3EF1C7]/10', low_stock: 'bg-[#FFC857]/10', out_of_stock: 'bg-[#F55454]/10' };

  const filtered = inventory.filter(i => {
    if (search && !i.sku.toLowerCase().includes(search.toLowerCase()) && !i.product_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => sortBy === 'quantity' ? a.quantity - b.quantity : a.sku.localeCompare(b.sku));

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Inventory & Allocation</h1>
            <p className="text-sm text-[#9CB0C7]">Inventory Management</p>
          </div>
          <button onClick={() => navigate('/dashboard')} className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">Dashboard</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Alerts */}
        {(lowStockCount > 0 || outOfStockCount > 0) && (
          <div className="flex gap-4 mb-6">
            {lowStockCount > 0 && (
              <div className="flex-1 bg-[#FFC857]/5 border border-[#FFC857]/30 rounded-xl p-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-[#FFC857]" />
                <div>
                  <span className="text-[#FFC857] font-medium">{lowStockCount} items low stock</span>
                  <p className="text-xs text-[#9CB0C7]">Below reorder point</p>
                </div>
              </div>
            )}
            {outOfStockCount > 0 && (
              <div className="flex-1 bg-[#F55454]/5 border border-[#F55454]/30 rounded-xl p-4 flex items-center gap-3">
                <Package className="w-5 h-5 text-[#F55454]" />
                <div>
                  <span className="text-[#F55454] font-medium">{outOfStockCount} items out of stock</span>
                  <p className="text-xs text-[#9CB0C7]">Needs immediate restocking</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CB0C7]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by SKU or product name..." className="w-full bg-[#0E1426] border border-[#22304A] rounded-lg pl-10 pr-4 py-2.5 text-white text-sm" />
          </div>
          <select value={location} onChange={e => setLocation(e.target.value)} className="bg-[#0E1426] border border-[#22304A] rounded-lg px-3 py-2.5 text-white text-sm">
            <option value="all">All Locations</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name} ({l.city})</option>)}
          </select>
          <button onClick={() => setSortBy(sortBy === 'quantity' ? 'sku' : 'quantity')} className="flex items-center gap-1 px-3 py-2.5 border border-[#22304A] text-[#9CB0C7] rounded-lg text-sm hover:text-white transition">
            <ArrowUpDown className="w-3 h-3" /> Sort: {sortBy}
          </button>
        </div>

        {/* Inventory Table */}
        <div className="bg-[#0E1426] border border-[#22304A] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#22304A]">
                <th className="text-left p-4 text-xs text-[#9CB0C7] font-medium">SKU</th>
                <th className="text-left p-4 text-xs text-[#9CB0C7] font-medium">Product</th>
                <th className="text-left p-4 text-xs text-[#9CB0C7] font-medium">Location</th>
                <th className="text-right p-4 text-xs text-[#9CB0C7] font-medium">Quantity</th>
                <th className="text-right p-4 text-xs text-[#9CB0C7] font-medium">Reorder Point</th>
                <th className="text-center p-4 text-xs text-[#9CB0C7] font-medium">Status</th>
                <th className="text-right p-4 text-xs text-[#9CB0C7] font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item.sku} className="border-b border-[#22304A]/50 hover:bg-[#7A5CFF]/5">
                  <td className="p-4 text-sm text-white font-mono">{item.sku}</td>
                  <td className="p-4 text-sm text-white">{item.product_name}</td>
                  <td className="p-4 text-xs text-[#9CB0C7] flex items-center gap-1"><MapPin className="w-3 h-3" />{item.location}</td>
                  <td className={`p-4 text-sm text-right font-bold ${statusColor[item.status]}`}>{item.quantity}</td>
                  <td className="p-4 text-sm text-right text-[#9CB0C7]">{item.reorder_point}</td>
                  <td className="p-4 text-center"><span className={`px-2 py-0.5 rounded text-[10px] font-medium ${statusBg[item.status]} ${statusColor[item.status]}`}>{item.status.replace('_', ' ')}</span></td>
                  <td className="p-4 text-right">
                    <button onClick={() => setShowAdjust(item.sku)} className="px-2 py-1 bg-[#7A5CFF]/10 text-[#7A5CFF] rounded text-xs hover:bg-[#7A5CFF]/20 transition">Adjust</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Adjust Modal */}
        {showAdjust && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowAdjust(null)}>
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white mb-4">Adjust Inventory: {showAdjust}</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <button onClick={() => setAdjustQty(adjustQty - 1)} className="w-10 h-10 border border-[#22304A] rounded-lg flex items-center justify-center text-white hover:bg-[#22304A]"><Minus className="w-4 h-4" /></button>
                  <input value={adjustQty} onChange={e => setAdjustQty(parseInt(e.target.value) || 0)} type="number" className="flex-1 bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-center text-lg" />
                  <button onClick={() => setAdjustQty(adjustQty + 1)} className="w-10 h-10 border border-[#22304A] rounded-lg flex items-center justify-center text-white hover:bg-[#22304A]"><Plus className="w-4 h-4" /></button>
                </div>
                <select value={adjustReason} onChange={e => setAdjustReason(e.target.value)} className="w-full bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm">
                  <option value="count">Physical Count</option>
                  <option value="received">Received Shipment</option>
                  <option value="damaged">Damaged/Defective</option>
                  <option value="returned">Customer Return</option>
                  <option value="correction">Correction</option>
                </select>
                <button onClick={() => adjustInventory(showAdjust)} className="w-full py-2 bg-[#7A5CFF] text-white rounded-lg font-medium">Apply Adjustment</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
